export type ThemeId = "classic" | "soft" | "vice";

export interface Palette {
  id: ThemeId;
  name: string;
  tagline: string;

  bg: string;
  bgAlt: string;
  card: string;
  fg: string;
  fgMuted: string;
  fgFaint: string;
  border: string;
  rule: string;
  shadowColor: string;

  accent: string;
  accentFg: string;
  danger: string;
  dangerFg: string;
  success: string;
  successFg: string;
  info: string;
  infoFg: string;

  boardLight: string;
  boardDark: string;
  boardLightPiece: string;
  boardDarkPiece: string;
  boardPieceStroke: string;
  mistakeFrom: string;
  mistakeTo: string;
  selected: string;
  moveCandidate: string;

  // CHOICE drill option colors — three visually distinct, semantically NEUTRAL
  // hues (deliberately not success/danger) so the color of an option never
  // hints whether it's correct. Used for both the board arrows and the buttons.
  choiceA: string;
  choiceB: string;
  choiceC: string;

  // Eval chart
  evalFillWhite: string; // fill above 0 (White winning) — must stand out from chart bg
  evalFillBlack: string; // fill below 0 (Black winning)
  mistakeWhite: string; // chart marker line for a White mistake
  mistakeBlack: string; // chart marker line for a Black mistake
}

export const PALETTES: Record<ThemeId, Palette> = {
  classic: {
    id: "classic",
    name: "Classic",
    tagline: "cream · ink · electric yellow",
    bg: "#f3eee2",
    bgAlt: "#e8e1cf",
    card: "#ffffff",
    fg: "#0a0a0a",
    fgMuted: "#3d3a32",
    fgFaint: "#6b6555",
    border: "#0a0a0a",
    rule: "#0a0a0a",
    shadowColor: "#0a0a0a",
    accent: "#ffe500",
    accentFg: "#0a0a0a",
    danger: "#ff2d2d",
    dangerFg: "#ffffff",
    success: "#1ec773",
    successFg: "#0a0a0a",
    info: "#3457ff",
    infoFg: "#ffffff",
    boardLight: "#f0d9b5",
    boardDark: "#b58863",
    boardLightPiece: "#f7f4ee",
    boardDarkPiece: "#1a1a1a",
    boardPieceStroke: "#0a0a0a",
    mistakeFrom: "rgba(255, 229, 0, 0.85)",
    mistakeTo: "#ff2d2d",
    selected: "#3457ff",
    moveCandidate: "rgba(30, 199, 115, 0.6)",
    choiceA: "#3457ff",
    choiceB: "#ff7a00",
    choiceC: "#9b5de5",
    evalFillWhite: "#d8dde6",
    evalFillBlack: "#0a0a0a",
    mistakeWhite: "#ff7a00",
    mistakeBlack: "#ff2d2d",
  },
  soft: {
    id: "soft",
    name: "Soft",
    tagline: "pale paper · muted greys · butter",
    bg: "#f6f1ea",
    bgAlt: "#ece5d8",
    card: "#ffffff",
    fg: "#4a4036",
    fgMuted: "#8a7f70",
    fgFaint: "#b5a995",
    border: "#a89e8c",
    rule: "#cdc3ad",
    shadowColor: "#c8bdaa",
    accent: "#f3c97a",
    accentFg: "#4a4036",
    danger: "#d77878",
    dangerFg: "#ffffff",
    success: "#8fb88a",
    successFg: "#3d4a3b",
    info: "#8aa1cf",
    infoFg: "#ffffff",
    boardLight: "#faf4e8",
    boardDark: "#9a8d72",
    boardLightPiece: "#fdf8ed",
    boardDarkPiece: "#3d342a",
    boardPieceStroke: "#3d342a",
    mistakeFrom: "rgba(243, 201, 122, 0.7)",
    mistakeTo: "#d77878",
    selected: "#8aa1cf",
    moveCandidate: "rgba(143, 184, 138, 0.55)",
    choiceA: "#8aa1cf",
    choiceB: "#d99a5c",
    choiceC: "#a98ac0",
    evalFillWhite: "#d7cfbf",
    evalFillBlack: "#4a4036",
    mistakeWhite: "#d99a5c",
    mistakeBlack: "#d77878",
  },
  vice: {
    id: "vice",
    name: "Vice",
    tagline: "miami pink · teal · cyan · lime",
    bg: "#ffd2e0",
    bgAlt: "#ffb8d1",
    card: "#ffffff",
    fg: "#0a3a44",
    fgMuted: "#4a7782",
    fgFaint: "#86a5ae",
    border: "#0a3a44",
    rule: "#0a3a44",
    shadowColor: "#ff2d8b",
    accent: "#00d4d4",
    accentFg: "#0a3a44",
    danger: "#ff006e",
    dangerFg: "#ffffff",
    success: "#39ff14",
    successFg: "#0a3a44",
    info: "#00b8c4",
    infoFg: "#0a3a44",
    boardLight: "#ffe8f0",
    boardDark: "#00a5b5",
    boardLightPiece: "#ffffff",
    boardDarkPiece: "#0a3a44",
    boardPieceStroke: "#0a3a44",
    mistakeFrom: "rgba(57, 255, 20, 0.65)",
    mistakeTo: "#ff006e",
    selected: "#00d4d4",
    moveCandidate: "rgba(57, 255, 20, 0.45)",
    choiceA: "#00d4d4",
    choiceB: "#ff8c1a",
    choiceC: "#c77dff",
    evalFillWhite: "#9fe8ee",
    evalFillBlack: "#0a3a44",
    mistakeWhite: "#ff8c1a",
    mistakeBlack: "#ff006e",
  },
};

export const TYPE = {
  display: '"Space Grotesk", system-ui, sans-serif',
  body: '"Space Grotesk", "Inter", -apple-system, system-ui, sans-serif',
  mono: '"Space Mono", "JetBrains Mono", ui-monospace, monospace',
} as const;

export const offsetShadow = (p: Palette, x = 4, y = 4): string =>
  `${x}px ${y}px 0 ${p.shadowColor}`;

export const THEME_IDS: readonly ThemeId[] = ["classic", "soft", "vice"];
